import "./SundayClosedModal.css";

/**
 * SundayClosedModal
 *
 * Friendly heads-up shown on /home when it is Sunday (IST).
 * Dailydish's kitchen is closed on Sundays — the menu already shows
 * Monday and preorders for Monday are open. This popup just
 * explains that warmly. Fully self-contained, no image assets.
 *
 * Props:
 *   isOpen      {boolean}
 *   mondayLabel {string}   — e.g. "Mon, 13 Jul" (shown in the copy)
 *   onClose     {function} — called on dismiss / overlay tap
 */

// Reusable botanical sprig used in the header decoration and the badge.
const Sprig = ({ className }) => (
  <svg className={className} viewBox="0 0 44 68" fill="none" aria-hidden="true">
    <path
      d="M22 66 C21 48 20 30 25 5"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      fill="none"
    />
    <path d="M22 44 C13 44 7 37 8 28 C17 29 24 36 22 44Z" fill="currentColor" />
    <path d="M23 35 C32 35 39 28 38 19 C29 20 22 27 23 35Z" fill="currentColor" />
    <path d="M22 27 C14 26 9 19 11 11 C19 13 25 20 22 27Z" fill="currentColor" />
    <path d="M23 19 C31 18 37 12 37 4 C29 6 22 12 23 19Z" fill="currentColor" />
    <path d="M24 11 C30 9 34 4 35 -2 C29 -1 24 4 24 11Z" fill="currentColor" />
  </svg>
);

const SundayClosedModal = ({ isOpen, mondayLabel, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fcm-overlay" onClick={onClose}>
      <div
        className="fcm-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="We are closed on Sundays"
      >
        {/* ── Green decorative top band with white dome ── */}
        <div className="fcm-topband">
          {/* white dome that blends into the body */}
          <svg
            className="fcm-arch"
            viewBox="0 0 380 150"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path d="M0 150 L0 112 Q190 30 380 112 L380 150 Z" />
          </svg>

          {/* botanical sprigs */}
          <Sprig className="fcm-sprig fcm-sprig--tl" />
          <Sprig className="fcm-sprig fcm-sprig--tr" />
          <Sprig className="fcm-sprig fcm-sprig--r" />

          {/* sparkles */}
          <span className="fcm-spark fcm-spark--1" aria-hidden="true">✦</span>
          <span className="fcm-spark fcm-spark--2" aria-hidden="true">✦</span>
          <span className="fcm-spark fcm-spark--3" aria-hidden="true">·</span>
          <span className="fcm-spark fcm-spark--4" aria-hidden="true">·</span>
        </div>

        {/* ── Close ── */}
        <button className="fcm-close-btn" onClick={onClose} aria-label="Close">
          <svg width="13" height="13" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>

        {/* ── Sleeping sprig badge ── */}
        <div className="fcm-badge" aria-hidden="true">
          <Sprig className="fcm-badge-sprig" />
          <span className="fcm-badge-zzz">
            <em className="fcm-z fcm-z--sm">z</em>
            <em className="fcm-z fcm-z--lg">Z</em>
          </span>
        </div>

        {/* ── Body ── */}
        <div className="fcm-body">
          <p className="fcm-eyebrow">Happy Sunday!</p>
          <h2 className="fcm-title">
            Our kitchen
            <br />
            is resting today
          </h2>

          <div className="fcm-divider" aria-hidden="true">
            <span className="fcm-divider-line" />
            <span className="fcm-divider-heart">♥</span>
            <span className="fcm-divider-line" />
          </div>

          <p className="fcm-copy">
            We&apos;re <strong>closed on Sundays</strong> to recharge — so every dish
            is cooked fresh for you again tomorrow.
          </p>

          <div className="fcm-callout">
            <div className="fcm-callout-tile" aria-hidden="true">
              <span>📅</span>
            </div>
            <div className="fcm-callout-text">
              <span className="fcm-callout-lead">Preorders are open for Monday</span>
              <span className="fcm-callout-sub">
                The menu below is all set for{" "}
                <strong>{mondayLabel || "Monday"}</strong> — reserve your meals now.
              </span>
            </div>
          </div>

          {/* <div className="fcm-chips">
            <span className="fcm-chip fcm-chip--muted">
              <span className="fcm-dot fcm-dot--red" /> Closed today
            </span>
            <span className="fcm-chip fcm-chip--live">
              <span className="fcm-dot fcm-dot--green" /> Preorder open for Mon
            </span>
          </div> */}

          <button className="fcm-cta" onClick={onClose}>
            <svg className="fcm-cta-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="4" y="3" width="16" height="18" rx="2" />
              <line x1="8" y1="8" x2="16" y2="8" />
              <line x1="8" y1="12" x2="16" y2="12" />
              <line x1="8" y1="16" x2="13" y2="16" />
            </svg>
            <span className="fcm-cta-label">Browse Monday&apos;s menu</span>
            <svg className="fcm-cta-chev" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>

          {/* <p className="fcm-footer">
            <span className="fcm-footer-leaf" aria-hidden="true">🌿</span>
            Thank you for your patience and support!
          </p>
          <div className="fcm-footer-heart" aria-hidden="true">♥</div> */}
        </div>

        {/* ── Faint corner foliage ── */}
        <span className="fcm-corner-blob" aria-hidden="true" />
        <Sprig className="fcm-sprig fcm-sprig--bl" />
        <Sprig className="fcm-sprig fcm-sprig--br" />
      </div>
    </div>
  );
};

export default SundayClosedModal;
